const visitStatuses = {
  COMPLETED: '已完成（正式提交）',
  DRAFT: '草稿（未提交）',
  AI_PROCESSING: 'AI 处理中',
  NEED_SUPPLEMENT: '待补充内容',
  NEED_MODIFY: '待修改内容',
  WAIT_CONFIRM: '待人工确认',
  CONFIRMED: '已确认',
  SUBMITTED: '已提交'
}

const complianceStatuses = {
  PASS: '校验通过',
  FAIL: '校验未通过',
  WARNING: '需注意',
  PENDING: '待校验'
}

const matchStatuses = {
  MATCHED: '已匹配主数据',
  AMBIGUOUS: '存在多个候选',
  UNMATCHED: '未匹配主数据',
  CONFLICT: '匹配结果冲突'
}

const aiStatuses = {
  PASS: 'AI 判断通过',
  FAIL: 'AI 判断未通过',
  WARNING: 'AI 提示注意',
  UNKNOWN: 'AI 未确定',
  SUCCESS: 'AI 已识别',
  EMPTY: 'AI 未提取'
}

const labelFor = (value, labels, empty) => {
  if (value == null || String(value).trim() === '') return empty
  const code = String(value).trim().toUpperCase()
  return labels[code] || `其他状态（${value}）`
}

export const visitStatusLabel = value => labelFor(value, visitStatuses, '状态未记录')
export const complianceStatusLabel = value => labelFor(value, complianceStatuses, '尚未校验')
export const checkStatusLabel = value => labelFor(value, { PASS: '通过', FAIL: '未通过', WARNING: '需注意', PENDING: '待校验' }, '未记录')
export const matchStatusLabel = value => labelFor(value, matchStatuses, '未记录匹配结果')
export const aiStatusLabel = value => labelFor(value, aiStatuses, '未记录 AI 判断')

export const hasVisitLocation = visit => [visit.latitude, visit.longitude].every(value =>
  value != null && String(value).trim() !== '' && Number.isFinite(Number(value)))

const isGpsCheck = check => ['gps_location', 'gps'].includes(String(check.check_code || '').toLowerCase()) ||
  /^GPS\s*定位$/i.test(String(check.check_name || ''))

const savedComplianceChecks = visit => {
  if (Array.isArray(visit.compliance_checks)) return visit.compliance_checks
  // Module lists contain the serialized AI snapshot rather than detail check rows.
  try {
    const snapshot = typeof visit.ai_compliance === 'string' ? JSON.parse(visit.ai_compliance) : visit.ai_compliance
    if (!snapshot || !Array.isArray(snapshot.checks)) return []
    return snapshot.checks.map(check => {
      const status = String(check.status || '').toLowerCase()
      return {
        check_code: check.key, check_name: check.label, display_value: check.display_value,
        check_status: status === 'pass' ? 'PASS' : status === 'warning' ? 'WARNING' : 'FAIL'
      }
    })
  } catch (e) { return [] }
}

const adjustSavedGpsSummary = (visit, result) => {
  if (!hasVisitLocation(visit)) return result
  const reasons = String(result.failSummary).split(/[；;\r\n]+/).map(value => value.trim()).filter(Boolean)
  const remaining = reasons.filter(reason => !/^(?:GPS\s*定位|gps_location|gps)\s*[:：]/i.test(reason))
  const removed = reasons.length - remaining.length
  if (!removed) return result
  // Old list records may lack the serialized checks. Remove only the named GPS failure.
  result.failCount = Math.max(remaining.length, (Number(result.failCount) || reasons.length) - removed, 0)
  result.failSummary = remaining.join('；')
  if (result.failCount) result.status = 'FAIL'
  else if (result.status === 'FAIL') result.status = 'PASS'
  return result
}

// Keep the saved AI snapshot intact; GPS display follows the saved device coordinates, as in APP.
export const visitComplianceDisplay = visit => {
  const located = hasVisitLocation(visit)
  const checks = savedComplianceChecks(visit).map(check => isGpsCheck(check)
    ? Object.assign({}, check, {
      check_status: located ? 'PASS' : 'FAIL',
      normalized_status: located ? 'PASS' : 'FAIL',
      display_value: located ? '通过' : '待获取',
      fail_reason: located ? '' : '待获取',
      evidence_summary: located ? '已保存定位经纬度' : '尚未保存有效定位经纬度',
      rule_reference: located ? '已保存定位经纬度' : '尚未保存有效定位经纬度'
    }) : check)
  const result = {
    status: visit.compliance_status,
    failCount: visit.compliance_fail_count || 0,
    failSummary: visit.compliance_fail_summary || '',
    checks
  }
  if (!checks.length) return adjustSavedGpsSummary(visit, result)
  const latestSeq = Math.max(...checks.map(check => Number(check.check_seq) || 0))
  const latest = checks.filter(check => (Number(check.check_seq) || 0) === latestSeq)
  if (!latest.some(isGpsCheck)) return result
  const status = check => String(check.check_status || check.normalized_status || '').toUpperCase()
  const failed = latest.filter(check => status(check) === 'FAIL')
  result.failCount = failed.length
  result.failSummary = failed.map(check => `${check.check_name || check.check_code}：${check.display_value || check.fail_reason || '不通过'}`).join('；')
  result.status = failed.length ? 'FAIL' : latest.some(check => status(check) === 'WARNING') ? 'WARNING'
    : latest.every(check => status(check) === 'PASS') ? 'PASS' : 'PENDING'
  return result
}

export const visitListDisplayFields = visit => {
  const compliance = visitComplianceDisplay(visit)
  return Object.assign({}, visit, {
    gps_status: hasVisitLocation(visit) ? 'PASS' : 'PENDING',
    compliance_status: compliance.status,
    compliance_fail_count: compliance.failCount,
    compliance_fail_summary: compliance.failSummary
  })
}
